































































































































































import ProjectHead from '../components/ProjectHead.vue';
import ComputerEmulator from '../components/ComputerEmulator.vue';
import SourceCode from '../components/SourceCode.vue';
import TabView from '../components/TabView.vue';
import TabItem from '../components/TabItem.vue';
import MemoryDebugger from '../components/MemoryDebugger.vue';
import DeviceCpu from '../components/DeviceCpu.vue';
import DeviceVideo from '../components/DeviceVideo.vue';
import DeviceKeyboard from '../components/DeviceKeyboard.vue';
import DeviceJoystick from '../components/DeviceJoystick.vue';
import DeviceTimer from '../components/DeviceTimer.vue';
import emulator, {Emulator} from '../emulator/Emulator';
import Vue from 'vue';
import Project, {ProjectFile} from '@/types/Project';
import EmulatorInfo, {emptyEmulatorInfo} from '@/emulator/EmulatorInfo';
import {Breakpoint} from '@/types/Breakpoint';
import FileLine from '@/components/types/FileLine';

export default Vue.extend({
    name: 'debugger',
    components: {
        ProjectHead, ComputerEmulator, SourceCode, TabView, TabItem,
        MemoryDebugger, DeviceCpu, DeviceVideo, DeviceKeyboard,
        DeviceJoystick, DeviceTimer,
    },

    //
    // DATA
    //

    data() : {
        running: boolean,
        state: EmulatorInfo,
        memoryBlock: number,
        visible: boolean,
        version: string,
        currentSourceFile: string,
        currentError?: string,
        binarySize: number,
    } {
        return {
            running: false,
            state: emptyEmulatorInfo(),
            memoryBlock: 0,
            visible: true,
            version: this.$store.state.project.projectDef.project.archVersion,
            currentSourceFile: 'main.s',
            currentError: undefined,
            binarySize: 0,
        };
    },

    computed: {
        project() : Project {
            return this.$store.state.project!.projectDef!.project;
        },

        files(): ProjectFile[] {
            return this.$store.state.project!.files!;
        },

        breakpointList() : number[] {
            return this.state.breakpoints!.filter((i) => i.filename === this.currentSourceFile).map((i: Breakpoint) => i.line);
        },

        content() : string {
            if (this.currentSourceFile === 'Messages')
                return this.messages;
            return this.files.find((f) => f.name === this.currentSourceFile)!.content as string;
        },

        messages() : string {
            if (this.running)
                return 'Running...';
            return this.currentError ?? 'No errors';
        },

        currentSourceLocation() : number {
            if (this.currentSourceFile === this.state.cpu.currentSourceLocation?.filename! && !this.running)
                return this.state.cpu.currentSourceLocation!.lineNumber - 1;
            else
                return -1;
        },

        fileList(): string[] {
            const r: string[] = this.$store.state.project!.files!
                .map((file: ProjectFile) => file.name)
                .filter((fn: string) => fn.endsWith('.s') || fn.endsWith('.def'));
            r.push('Messages');
            return r;
        },
    },

    //
    // METHODS
    //

    methods: {
        emulatorInitialized(e: Emulator) : void {   // called by computer-emulator when it finishes initializing
            this.loadEmulatorMemory(e);
            this.initializeBreakpoints(e);
            this.updateEmulatorState(e);
            e.addBreakpointListener(() => {
                e.stopMainLoop();
                this.running = false;
                this.updateEmulatorState(e);
            });
            e.screenUpdate();
            if (this.$route.query.run)
                this.run(e);
        },

        loadEmulatorMemory(e: Emulator) : void {
            const outputFile = this.files.filter((f) => f.name === 'output.bin');
            if (outputFile.length === 0) {
                console.error(`File 'output.bin' not present in project.`);
            } else {
                e.loadMemory(outputFile[0].content as number[]);
                const dbgFile = this.files.filter((f) => f.name === 'output.dbg')[0];
                e.loadDebuggingInfo(JSON.parse(dbgFile.content as string));
                this.binarySize = outputFile[0].content.length;
            }
        },

        async step() : Promise<void> {
            const e = await emulator(this.version, 'canvas');
            e.step();
            this.updateEmulatorState(e);
        },

        async reset() : Promise<void> {
            const e = await emulator(this.version, 'canvas');
            e.reset();
            this.loadEmulatorMemory(e);
            this.updateEmulatorState(e);
        },

        selectFileManually(filename: string) : void {
            this.currentSourceFile = filename;
        },

        initializeBreakpoints(e: Emulator) : void {
            const bkps : Breakpoint[] = [];
            for (const bkp of this.$store.state.breakpoints.list) {
                try {
                    e.swapBreakpoint(bkp.filename, bkp.line);
                    bkps.push(bkp);
                } catch (_) {
                    /* ignore */
                }
            }
            this.$store.commit('breakpoints/REPLACE', bkps);
        },

        async keyPressed(key: number) : Promise<void> {
            const e = await emulator(this.version, 'canvas');
            e.sendKeypress(key);
            this.updateEmulatorState(e, false);
        },

        async joystickChange(state: number) : Promise<void> {
            const e = await emulator(this.version, 'canvas');
            e.setJoystickState(state);
            this.updateEmulatorState(e, false);
        },

        async next() : Promise<void> {
            const e = await emulator(this.version, 'canvas');
            if (e.nextInstructionIsSubroutine()) {
                e.setBreakNext();
                this.setRunning(true);
                e.mainLoop(() => this.stopRunning(e));
            } else {
                await this.step();
            }
        },

        stopRunning(e: Emulator): void {
            e.stopMainLoop();
            this.currentSourceFile = 'Messages';
            this.running = false;
            this.updateEmulatorState(e);
        },

        run(e: Emulator) : void {
            this.setRunning(true);
            e.mainLoop(() => this.stopRunning(e));
        },

        async runPause() : Promise<void> {
            const e = await emulator(this.version, 'canvas');
            if (this.running) {
                this.stopRunning(e);
            } else {
                this.run(e);
            }
        },

        async runToNextFrame() : Promise<void> {
            const e = await emulator(this.version, 'canvas');
            e.addBreakAtEndOfFrame();
            this.setRunning(true);
            e.mainLoop(() => this.stopRunning(e));
        },

        async swapBreakpoint(pos: FileLine) : Promise<void> {
            const e = await emulator(this.version, 'canvas');
            e.swapBreakpoint(pos.filename, pos.line);
            await this.updateEmulatorState(e, false);
        },

        async updateMemoryBlock(newBlock: number) : Promise<void> {
            this.memoryBlock = newBlock;
            this.updateEmulatorState((await emulator(this.version, 'canvas')), false);
        },

        updateEmulatorState(e: Emulator, updateCurrentFile: boolean = true) : void {
            // update state
            this.state = Object.assign(e.debuggerState(this.memoryBlock));
            if (process.env.NODE_ENV === 'development') {
                console.log('Emulator debugger state updated:');
                console.log(this.state);
            }
            // replace breakpoints, if changed
            this.$store.commit('breakpoints/REPLACE', this.state.breakpoints);
            // update errors
            const emulatorDefinedSourceFile = this.state.cpu.error ? 'Messages' : this.state.cpu.currentSourceLocation!.filename;
            this.currentError = undefined;
            if (emulatorDefinedSourceFile === '')
                this.currentError = 'No debugging information found for this location\n(the PC is currently not pointing to a valid line of code).';
            if (this.state.cpu.error)
                this.currentError = this.state.cpu.error!;
            // update file being viewed
            if (updateCurrentFile) {
                this.currentSourceFile = emulatorDefinedSourceFile;
                if (this.currentSourceFile === '')
                    this.currentSourceFile = 'Messages';
            }
            setTimeout((emu) => emu.screenUpdate(), 0, e);
        },

        async returnToEditor() : Promise<void> {
            this.running = false;
            const e = await emulator(this.version, 'canvas');
            e.stopMainLoop();
            e.destroy();
            console.log('Emulator destroyed.');
            await this.$router.push(`/editor/${this.$store.state.project!.projectDef!.id}`);
        },

        setRunning(v: boolean) : void {
            this.running = v;
            if (v)
                this.currentSourceFile = 'Messages';
        },

        maxRomSize() : number {
            return 0xE200;
        },
    },
});
